<template>
  <div>
    <async-loader v-if="memberDetailsLoading || ( restoredFromTransaction && memberTransactionDetailsLoading )" />
    <form v-else @submit.prevent="submitAndContinue">
      <app-step-dependent
        :savingDraft="savingDraft"
        @saveDraftClicked="saveDraft"
      >
        <h2>Add Dependent"</h2>
      </app-step-dependent>

      <div container>
        <list-error-warnings class="_mbs" :warnings="listErrorWarnings" />
      </div>

      <div container>
        <div grid>
          <div column="3">
            <client-health-id
              v-model="clientId"
              :enable-mask="ssnMatchesClientHealthId($v.ssn.$model, $v.clientId.$model)"
              :errorMessage="clientIdError"
              :invalid="$v.clientId.$invalid"
              valueKey="clientId"
              label="Client's Member ID:"
            />

          </div>
          <div column="3" v-if="isInternalUser">
            <app-input
              v-model="$v.fileBoundId.$model"
              :required="isInternalUser"
              :errorMessage="fileBoundIdError"
              :invalid="$v.fileBoundId.$invalid"
              valueKey="fileBoundId"
              label=" Filebound ID:"
            />
          </div>
        </div>
        <div grid class="_mts">
          <div column="3">
            <app-input
              v-model="$v.firstName.$model"
              :required="true"
              :errorMessage="firstNameError"
              :invalid="$v.firstName.$invalid"
              valueKey="firstName"
              label="First Name:"
            />
          </div>
          <div column="3">
            <app-input
              v-model="$v.middleInitial.$model"
              :errorMessage="middleInitialError"
              :invalid="$v.middleInitial.$invalid"
              valueKey="middleInitial"
              label="Middle Initial:"
            />
          </div>
          <div column="3">
            <app-input
              :required="true"
              v-model="$v.lastName.$model"
              :errorMessage="lastNameError"
              :invalid="$v.lastName.$invalid"
              valueKey="lastName"
              label="Last Name:"
            />
          </div>
          <div column="3">
            <app-input
              v-model="$v.suffix.$model"
              :invalid="$v.suffix.$invalid"
              :errorMessage="suffixError"
              valueKey="suffix"
              label="Suffix:"
            />
          </div>
        </div>
        <div grid class="_mts">
          <div column="3">
            <app-date-input
              v-model="$v.dob.$model"
              :required="true"
              :errorMessage="dobError"
              :invalid="$v.dob.$invalid"
              valueKey="dob"
              label="Date of Birth:"
            />
          </div>
          <div column="3">
            <app-select
              v-model="$v.relationship.$model"
              :required="true"
              :errorMessage="relationshipError"
              :invalid="$v.relationship.$invalid"
              :options="relationshipOptions"
              name="relationship"
              label="relationship:"
            />
          </div>
          <div column="3">
            <app-select
              :required="true"
              :options="genderOptions"
              name="gender"
              label="Gender:"
              :errorMessage="genderError"
              :invalid="$v.gender.$invalid"
              v-model="$v.gender.$model"
            />
          </div>
          <div column="3">
            <app-input
              v-model="$v.ssn.$model"
              :invalid="$v.ssn.$invalid"
              :errorMessage="ssnError"
              valueKey="ssn"
              label="SSN:"
            />
          </div>
        </div>
        <div grid class="_mts">
          <div column="3">
            <app-date-input
              :required="true"
              v-model="$v.effectiveDate.$model"
              :errorMessage="listErrorFilter('effectiveDate', effectiveDateError)"
              :invalid="$v.effectiveDate.$invalid"
              name="effectiveDate"
              label="Effective Date:"
            />
          </div>
          <div column="3">
            <app-date-input
              v-model="$v.termDate.$model"
              :errorMessage="listErrorFilter('termDate', termDateError)"
              :invalid="$v.termDate.$invalid"
              valueKey="termDate"
              label="Term Date:"
            />
          </div>
        </div>
      </div>
      <contact-information
        :canEditHomeAddress="false"
        :editable="true"
        :showForm="true"
        :showNameFields="false"
        :loading="memberDetailsLoading"
        :addresses="addresses"
        v-model="contactInformation"
        @ready="isReady => (formsReady.contact = isReady)"
      />

      <reporting-codes
        :memberDetailsLoading="false"
        :subGroupPlanInfo="computedCurrentSubGroupPlanInfo"
        v-model="reportingCodes"
        @ready="isReady => (formsReady.reporting = isReady)"
      />
      <privacy-address
        :memberDetailsLoading="false"
        v-model="privacyAddress"
        @ready="isReady => (formsReady.privacy = isReady)"
      />
      <language-assistance v-model="languageAssistance" class="_bb1" />

      <div v-if="submissionError" container>
        <div class="announcement -warning _mts">
          {{ submissionError }}
        </div>
      </div>

      <div class="_text-center _mts _mbs">
        <div container class="_text-center">
              <async-button
                type="submit"
                class="btn --sm"
                :executing="submitting"
                :disabled="!allFormsReady"
              >
                <template #buttonContent>
                  <span>Submit and Continue</span>
                </template>
              </async-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import AsyncButton from '@molecules/AsyncButton.vue'
  import AsyncLoader from '@molecules/AsyncLoader'
  import ContactInformation from '../MemberPartials/ContactInformation'
  import ReportingCodes from '../MemberEdit/ReportingCodes'
  import PrivacyAddress from '../MemberEdit/PrivacyAddress'
  import LanguageAssistance from '../MemberEdit/LanguageAssistance'
  import AppStepDependent from './Steps/StepDependent.vue'

  import AppInput from '@atoms/Input'
  import AppSelect from '@atoms/Select'
  import AppDateInput from '@atoms/DateInput'

  import MemberApi from '@api/MemberApi'
  import { MEMBER_ADD_SUCCESSFUL } from '@api/Statuses'

  import SubmitMember from '@mixins/SubmitMember'
  import SelectOptions from '@mixins/SelectOptions'
  import auth from '@mixins/auth'
  import validationMessages from '@mixins/validationMessages'
  import ClassPlanFromSubGroupCkData from '@mixins/ClassPlanFromSubGroupCkData'
  import formReady from '@mixins/formReady'
  import { required, requiredIf, maxLength, alphaNum } from 'vuelidate/lib/validators'
  import formattable from '@mixins/formattable'
  import CoverageDatesHelper from '@mixins/CoverageDatesHelper'
  import ListErrorWarnings from '@mixins/ListErrorWarnings'

  import { fourOrNineDigitsLong, after, onOrAfter, onOrBefore, nameAlphaNumeric, relationshipCodeToGenderCode, errToString, fileBoundIdValidation } from '@/utils'
  import moment from 'moment'

  import restoreMemberTransaction from '@mixins/restoreMemberTransaction'

  import clientHealthId from '@mixins/clientHealthId'
  import ClientHealthId from '@views/MemberManagement/MemberEdit/ClientHealthId'

  export default {
    mixins: [
      validationMessages,
      SubmitMember,
      SelectOptions,
      ClassPlanFromSubGroupCkData,
      auth,
      formReady,
      formattable,
      CoverageDatesHelper,
      ListErrorWarnings,
      restoreMemberTransaction,
      clientHealthId
    ],
    components: {
      ClientHealthId,
      AppStepDependent,
      AppInput,
      AppSelect,
      AppDateInput,
      ContactInformation,
      ReportingCodes,
      PrivacyAddress,
      LanguageAssistance,
      AsyncButton,
      AsyncLoader
    },
    data() {
      return {
        eyemedMemberId: '',
        memberDetailsLoading: false,
        submitting: false,
        savingDraft: false,
        fileBoundId: '',
        clientId: '',
        firstName: '',
        middleInitial: '',
        lastName: '',
        suffix: '',
        dob: '',
        gender: '',
        ssn: '',
        effectiveDate: '',
        termDate: '',
        relationship: '',
        contactInformation: {},
        reportingCodes: {
          divisionCode: '',
          benefitOption: '',
          locationCode: '',
          companyCode: '',
          clientGroupNumber: '',
          reportingCode1: '',
          reportingCode2: '',
          reportingCode3: '',
          reportingCode4: '',
          reportingCode5: ''
        },
        privacyAddress: {
          firstName: '',
          lastName: '',
          middleInitial: '',
          suffix: '',
          line1: '',
          line2: '',
          line3: '',
          zipCode: '',
          city: '',
          state: '',
          county: '',
          phoneNumber: '',
          emailAddress: ''
        },
        languageAssistance: {},
        primarySubscriberDetails: {
          member: {}
        },
        groupCk: '',
        subGroupCk: '',
        classId: '',
        planId: '',
        subscriberCk: '',
        memberCk: '',
        formsReady: {
          contact: true,
          privacy: true,
          reporting: true
        },
        submissionError: '',
        listErrors: {
          effectiveDate: {
            prefix: 'Effective date',
            errors: {
              onOrAfterDOB: {},
              onOrAfterSubGroupEffectiveDate: {},
              onOrAfterClassEffectiveDate: {},
              onOrAfterPlanEffectiveDate: {},
              onOrBeforeClassTermDate: {},
              onOrBeforePlanTermDate: {},
              afterOriginalEffectiveDateOfPrimarySubscriber: {}
            }
          },
          termDate: {
            prefix: 'Term date',
            errors: {
              beforeTermDateOfPrimarySubscriber: {},
              afterEffectiveDate: {},
              onOrBeforeSubGroupTermDate: {},
              onOrBeforeClassTermDate: {},
              onOrBeforePlanTermDate: {}
            }
          }
        }
      }
    },
    validations: {
      firstName: {
        required,
        maxLength: maxLength(15),
        nameAlphaNumeric
      },
      middleInitial: {
        maxLength: maxLength(1)
      },
      lastName: {
        required,
        maxLength: maxLength(35),
        nameAlphaNumeric
      },
      suffix: {
        maxLength: maxLength(10)
      },
      gender: {
        required
      },
      ssn: {
        fourOrNineDigitsLong
      },
      dob: {
        required
      },
      effectiveDate: {
        required,
        onOrAfterDOB: onOrAfter('dob'),
        onOrAfterSubGroupEffectiveDate: onOrAfter('subGroupEffectiveDate'),
        onOrAfterClassEffectiveDate: onOrAfter('classEffectiveDate'),
        onOrAfterPlanEffectiveDate: onOrAfter('planEffectiveDate'),
        onOrBeforeClassTermDate: onOrBefore('classTermDate'),
        onOrBeforePlanTermDate: onOrBefore('planTermDate'),
        afterOriginalEffectiveDateOfPrimarySubscriber(value, vm) {
          if (!value) { return true }

          if (!vm.primarySubscriberDetails ||
            !vm.primarySubscriberDetails.member ||
            !vm.primarySubscriberDetails.member.enrollmentDate) {
            return true
          }

          if (value.trim() === '' || !moment(value).isValid()) {
            return true
          }

          return moment(value).isSameOrAfter(vm.primarySubscriberDetails.member.enrollmentDate)
        }
      },
      termDate: {
        beforeTermDateOfPrimarySubscriber(value, vm) {
          if (!value) { return true }

          if (!vm.primarySubscriberDetails ||
            !vm.primarySubscriberDetails.member ||
            !vm.primarySubscriberDetails.member.termDate) {
            return true
          }

          if (value.trim() === '' || !moment(value).isValid()) {
            return true
          }

          return moment(value).isSameOrBefore(vm.primarySubscriberDetails.member.termDate)
        },
        afterEffectiveDate: after('effectiveDate'),
        onOrBeforeSubGroupTermDate: onOrBefore('subGroupTermDate'),
        onOrBeforeClassTermDate: onOrBefore('classTermDate'),
        onOrBeforePlanTermDate: onOrBefore('planTermDate')
      },
      relationship: {
        required
      },
      clientId: {
        required: requiredIf(function() {
          return this.computedMemberIdSource === 'CS'
        }),
        maxLength: maxLength(15),
        alphaNum
      },
      fileBoundId: {
        required: requiredIf(function() {
          return this.isInternalUser
        }),
        maxLength: maxLength(15),
        fileBoundIdValidation
      }
    },
    computed: {
      primarySubscriberMemberCk() {
        return this.$store.getters['hiddenRouteData/getHiddenRouteData'].primarySubscriberMemberCk
      },
      allFormsReady() {
        let ready = true
        Object.values(this.formsReady).map(formReady => {
          if (formReady != true) {
            ready = false
          }
        })
        if (this.ready != true) {
          ready = false
        }
        return ready
      },
      transactionId () {
        return this.$route.query.transactionId
      },
      primarySubscriberDetailsComesFromStore() {
        return !this.primarySubscriberMemberCk
      },
      fullName() {
        if (this.primarySubscriberDetailsComesFromStore) {
          return this.$store.getters['addFamily/fullName']
        } else if (!this.primarySubscriberDetails.member) {
          return ''
        }

        return `${this.primarySubscriberDetails.member.firstName} ${
          this.primarySubscriberDetails.member.lastName
        }`
      },
      selectedPlan() {
        return this.getFirstPlanByClassAndPlanId(this.classId, this.planId)
      },
      addresses() {
        if (!this.primarySubscriberDetails) {
          return []
        }

        if (!this.primarySubscriberDetailsComesFromStore) {
          return this.$store.getters.memberAddresses.map(address => ({
            ...address,
            ...{
            homePhoneNumber: address.phone,
            cellPhoneNumber: this.primarySubscriberDetails.member.cellPhoneNumber,
            workPhoneNumber: this.primarySubscriberDetails.member.workPhoneNumber + (this.primarySubscriberDetails.member.workPhoneNumberExt ? ` +${this.primarySubscriberDetails.member.workPhoneNumberExt}` : '')
          }
          }))
        } else {
          return []
        }
      }
    },
    created() {
      if (!this.primarySubscriberMemberCk) {
        this.$store.commit('SET_MEMBER_DETAILS_LOADING', false)
        return
      }
      if (!this.primarySubscriberDetailsComesFromStore) {
        new MemberApi({
          simulateExample: 'Member Details-With eligibility & benefits'
        })
          .memberDetails({
            params: {
              memberCk: this.primarySubscriberMemberCk,
              dateOfService: this.now(),
              isEligibility: true,
              isBenefit: true
            }
          })
          .then(memberDetails => {
            this.primarySubscriberDetails = memberDetails

            if (this.primarySubscriberDetails.member) {
              this.effectiveDate = this.primarySubscriberDetails.member.enrollmentDate
              this.eyemedMemberId = this.primarySubscriberDetails.member.eyemedMemberId
              this.fileBoundId = this.primarySubscriberDetails.member.fileBoundId
              this.clientId = this.primarySubscriberDetails.member.clientHealthId
              this.contactInformation = this.primarySubscriberDetails.member.homeAddress
              this.groupCk = this.primarySubscriberDetails.member.groupCk
              this.subGroupCk = this.primarySubscriberDetails.member.subgroupCk
              this.classId = this.primarySubscriberDetails.member.classId
              this.planId = this.primarySubscriberDetails.member.planId
              this.subscriberCk = this.primarySubscriberDetails.member.subscriberCk
              this.memberCk = this.primarySubscriberDetails.member.memberCk
              this.reportingCodes = {
                benefitOption: this.primarySubscriberDetails.member.benefitOption || this.reportingCodes.benefitOption,
                locationCode: this.primarySubscriberDetails.member.location || this.reportingCodes.locationCode,
                divisionCode: this.primarySubscriberDetails.member.divisionCode || this.reportingCodes.divisionCode,
                companyCode: this.primarySubscriberDetails.member.companyCode || this.reportingCodes.companyCode,
                clientGroupNumber: this.primarySubscriberDetails.member.clientGroupNbr || this.primarySubscriberDetails.member.clientGroupNumber || this.reportingCodes.clientGroupNumber,
                reportingCode1: this.primarySubscriberDetails.member.reportingCodes[0] || this.reportingCodes.reportingCode1,
                reportingCode2: this.primarySubscriberDetails.member.reportingCodes[1] || this.reportingCodes.reportingCode2,
                reportingCode3: this.primarySubscriberDetails.member.reportingCodes[2] || this.reportingCodes.reportingCode3,
                reportingCode4: this.primarySubscriberDetails.member.reportingCodes[3] || this.reportingCodes.reportingCode4,
                reportingCode5: this.primarySubscriberDetails.member.reportingCodes[4] || this.reportingCodes.reportingCode5
              }
            }

            this.$store.dispatch('GET_MEMBER_ADDRESSES', { memberCk: this.primarySubscriberDetails.member.subscriberCk })
          })
          .catch(error => {
            if (error.response) {
              error.response.json().then(responseBody => {
                this.submissionError = responseBody.userMessage
              })
            } else console.error(error)
          })
          .finally(() => {
            this.$store.commit('SET_MEMBER_DETAILS_LOADING', false)
          })
      } else {
        this.subGroupCk = this.$store.state.addFamily.subGroupCk
        this.fileBoundId = this.$store.state.addFamily.fileBoundId
        this.clientId = this.$store.state.addFamily.clientId
        this.contactInformation = this.$store.state.addFamily.contactInformation
        this.groupCk = this.$store.state.addFamily.groupCk
        this.classId = this.$store.state.addFamily.classId
        this.planId = this.$store.state.addFamily.planId
        this.subscriberCk = this.$store.state.addFamily.subscriberCk
        this.memberCk = this.$store.state.addFamily.memberCk

        this.$store.dispatch('GET_MEMBER_ADDRESSES', { memberCk: this.subscriberCk })
        this.requestClassPlansForSubGroup(this.subGroupCk)
      }
    },
    methods: {
      addDependent(type) {
        let payload = this.getMemberPayload(this)
        payload.memberIdSource = this.computedMemberIdSource
        payload.transactionType = type
        payload.memberType = 'Dependent'
        payload.subscriberCk = this.subscriberCk
        payload.subscriberMemberCk = this.memberCk
        payload.selectedPlanInfo = this.selectedPlan

        return new MemberApi({ simulateExample: 'Add Dependent' }).addMember(
          payload
        )
      },
      submitAndContinue() {
        this.submitting = true

        this.addDependent('Add')
          .then(res => {
            this.submitting = false

            if (res.status !== MEMBER_ADD_SUCCESSFUL) {
              console.warn('Member add not successful', res.status)
              // TODO: handle not successful
              return
            }

            const memberCkResults = res.userMessage.match(/(memberCk=([0-9]+))/)
            const memberCk = memberCkResults[2]
            if (!memberCkResults[2]) {
              console.warn(
                `Unable to get memberCk from add dependent response ${res}`
              )
              return
            }

            this.$router.push({
              name: 'member-details',
              params: {
                memberCk: memberCk
              }
            })
          })
          .catch(err => {
            errToString(err)
              .then((errStr) => {
                this.submissionError = errStr
              })
          })
          .finally(() => {
            this.submitting = false
          })
      },
      saveDraft() {
        this.savingDraft = true

        this.addDependent('Hold')
          .then(res => {
            this.savingDraft = false

            if (res.status !== MEMBER_ADD_SUCCESSFUL) {
              console.warn('Member add not successful', res.status)
              // TODO: handle not successful
              return
            }

            this.$router.push({ name: 'dashboard' })
          })
          .catch(err => {
            err.response.json().then(responseBody => {
              this.submissionError = responseBody.userMessage
            })
          })
          .finally(() => {
            this.savingDraft = false
          })
      }
    },
    watch: {
      relationship (newRelationship) {
        const genderCode = relationshipCodeToGenderCode(newRelationship)
        if (genderCode) {
          this.gender = genderCode
        }
      }
    }
  }
</script>
